import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSignature, Plus, Send, Trash2 } from 'lucide-react';
import {
  type Envelope,
  type EnvelopeCreated,
  type SignerDef,
  certificateUrl,
  createEnvelope,
  listEnvelopes,
  sendEnvelope,
} from '@/api/esign';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const STATUS_STYLES: Record<string, string> = {
  completed: 'bg-emerald-900/30 text-emerald-300',
  sent: 'bg-sky-900/30 text-sky-300',
  draft: 'bg-amber-900/30 text-amber-300',
  declined: 'bg-red-900/30 text-red-300',
};

export function ESignPage() {
  const { t } = useTranslation();
  const [envelopes, setEnvelopes] = useState<Envelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [created, setCreated] = useState<EnvelopeCreated | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    listEnvelopes()
      .then(setEnvelopes)
      .catch(() => setEnvelopes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function send(id: string) {
    await sendEnvelope(id);
    load();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('esign.title')}</h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('esign.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="esign-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('esign.new')}
        </Button>
      </header>

      <div className="overflow-hidden rounded-lg border bg-card">
        {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && envelopes.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">{t('esign.empty')}</p>
        )}
        <ul className="divide-y divide-[var(--color-lop-border)]">
          {envelopes.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid="esign-row">
              <FileSignature className="h-5 w-5 shrink-0 text-[var(--color-lop-text-muted)]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">{e.title}</p>
                <p className="text-xs text-[var(--color-lop-text-muted)]">{e.createdAt.slice(0, 10)}</p>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                  STATUS_STYLES[e.status] ?? 'bg-zinc-800 text-zinc-300'
                }`}
              >
                {t(`esign.status.${e.status}`, e.status)}
              </span>
              {e.status === 'draft' && (
                <Button size="sm" variant="outline" onClick={() => send(e.id)} data-testid="esign-send">
                  <Send className="mr-1 h-4 w-4" />
                  {t('esign.send')}
                </Button>
              )}
              {e.status === 'completed' && (
                <a
                  href={certificateUrl(e.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[var(--color-lop-teal)] hover:underline"
                >
                  {t('esign.certificate')}
                </a>
              )}
            </li>
          ))}
        </ul>
      </div>

      {showNew && (
        <NewEnvelopeModal
          onClose={() => setShowNew(false)}
          onCreated={(c) => {
            setShowNew(false);
            setCreated(c);
            load();
          }}
        />
      )}
      {created && <SignerLinksModal created={created} onClose={() => setCreated(null)} />}
    </div>
  );
}

function NewEnvelopeModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (c: EnvelopeCreated) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [signers, setSigners] = useState<SignerDef[]>([{ order: 1, name: '', email: '', role: 'signer' }]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function setSigner(i: number, patch: Partial<SignerDef>) {
    setSigners((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  async function submit() {
    const valid = signers
      .filter((s) => s.name.trim() && s.email.trim())
      .map((s, i) => ({ ...s, order: i + 1, name: s.name.trim(), email: s.email.trim() }));
    if (!title.trim() || valid.length === 0) {
      setError(t('esign.newModal.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onCreated(await createEnvelope({ title: title.trim(), signers: valid }));
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create envelope');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('esign.newModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('esign.newModal.documentTitle')}
        </label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mb-4" data-testid="esign-title" />
        <p className="mb-2 text-xs font-medium text-[var(--color-lop-text-secondary)]">{t('esign.newModal.signers')}</p>
        <div className="space-y-2">
          {signers.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <Input
                placeholder={t('esign.newModal.signerName')}
                value={s.name}
                onChange={(e) => setSigner(i, { name: e.target.value })}
              />
              <Input
                type="email"
                placeholder={t('esign.newModal.signerEmail')}
                value={s.email}
                onChange={(e) => setSigner(i, { email: e.target.value })}
              />
            </div>
          ))}
        </div>
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            className="text-xs text-[var(--color-lop-teal)] hover:underline"
            onClick={() => setSigners((p) => [...p, { order: p.length + 1, name: '', email: '', role: 'signer' }])}
          >
            + {t('esign.newModal.addSigner')}
          </button>
          {signers.length > 1 && (
            <button
              type="button"
              className="text-xs text-[var(--color-lop-text-muted)] hover:text-red-500"
              onClick={() => setSigners((p) => p.slice(0, -1))}
            >
              <Trash2 className="mr-1 inline h-3 w-3" />
              {t('common.remove')}
            </button>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="esign-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SignerLinksModal({ created, onClose }: { created: EnvelopeCreated; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-2 text-lg font-semibold">{t('esign.linksModal.title')}</h3>
        <p className="mb-3 text-sm text-[var(--color-lop-text-secondary)]">{t('esign.linksModal.subtitle')}</p>
        <ul className="space-y-2">
          {created.signers.map((s) => (
            <li key={s.signerId} className="rounded border border-[var(--color-lop-border)] p-2 text-xs">
              <p className="font-medium text-[var(--color-lop-text-primary)]">{s.email}</p>
              <code className="break-all text-[var(--color-lop-text-secondary)]">
                {`${window.location.origin}/sign/${s.accessToken}`}
              </code>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}
